import { addDays, areIntervalsOverlapping, format, isWithinInterval, parseISO } from 'date-fns';
import { balances, regularHistoryTemplate, timeOffEntries } from '../data/timeOff';
import type { Employee, Policy, PtoBalance, TimeOffHistoryRow } from '../types/hr';
import { TODAY } from './format';
import { getHRState } from './hrStore';

interface DatedRange {
  start: string;
  end: string;
}

const TODAY_ISO = format(TODAY, 'yyyy-MM-dd');

export function getPolicy(id?: string): Policy | undefined {
  return getHRState().policies.find((p) => p.id === id);
}

export function policyName(id?: string): string {
  return getPolicy(id)?.name ?? 'No policy';
}

export function isOutOn(range: DatedRange, date: Date): boolean {
  return isWithinInterval(date, { start: parseISO(range.start), end: parseISO(range.end) });
}

export function overlaps(range: DatedRange, start: Date, end: Date): boolean {
  return areIntervalsOverlapping({ start: parseISO(range.start), end: parseISO(range.end) }, { start, end }, { inclusive: true });
}

export function balanceFor(employeeId: string): PtoBalance | undefined {
  return balances.find((b) => b.employeeId === employeeId);
}

export function accrualStart(hireIso: string): Date {
  return addDays(parseISO(hireIso), 30);
}

/** Future approved days: scheduled time off plus requests approved this session. */
export function scheduledDays(employeeId: string): number {
  const fromEntries = timeOffEntries.filter((e) => e.employeeId === employeeId && e.start > TODAY_ISO).reduce((sum, e) => sum + e.days, 0);
  const fromRequests = getHRState()
    .requests.filter((r) => r.employeeId === employeeId && r.status === 'approved')
    .reduce((sum, r) => sum + r.days, 0);
  return fromEntries + fromRequests;
}

export function historyFor(employee: Employee): TimeOffHistoryRow[] {
  if (employee.policyId === 'regular') {
    const available = balanceFor(employee.id)?.available ?? 0;
    return regularHistoryTemplate.map((row) => ({ ...row, id: `${employee.id}-${row.id}`, balance: available + row.balance }));
  }
  if (employee.policyId === 'newhire') {
    const year = parseISO(employee.hireDate).getFullYear();
    return [
      {
        id: `${employee.id}-nh`,
        date: employee.hireDate,
        description: `Assigned to New Hire PTO — accrual begins ${format(accrualStart(employee.hireDate), 'MMM d, yyyy')}`,
        balance: 0,
        year,
      },
    ];
  }
  return [];
}

export function usedYtd(employee: Employee): number {
  return historyFor(employee)
    .filter((h) => h.year === TODAY.getFullYear())
    .reduce((sum, h) => sum + (h.used ?? 0), 0);
}
